import type { TFunction } from 'i18next';
import type { FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { Event, IRedirectDomain, RedirectDomain } from '@feathr/blackbox';
import { submitModelWithErrorHandling, toast } from '@feathr/components';
import { useLicense } from '@feathr/extender/hooks';
import { useAccount, useActionBar, useLocalUrl, useStore } from '@feathr/extender/state';
import { moment, momentToDate, TimeFormat } from '@feathr/hooks';
import type { ListResponse, TValidateGrouped } from '@feathr/rachis';

interface IAdvertiserOption {
  id: string;
  name: string;
}

/** Return type for the useEventSettingsInfo hook. */
interface IUseEventSettingsInfoReturn {
  /** The current project (event) model. */
  event: Event;
  /** Translation function. */
  t: TFunction;
  /** Builds an account-prefixed URL. */
  localUrl: ReturnType<typeof useLocalUrl>;
  /** Grouped validation errors for the project fields. */
  errors: TValidateGrouped;
  /** The schedule start date as a Date, for the date pickers' range linking. */
  startDate?: Date;
  /** The schedule end date as a Date, for the date pickers' range linking. */
  endDate?: Date;
  /** Whether the project model has unsaved changes. */
  isDirty: boolean;
  /** Whether the account is an agency (gates the default advertiser field). */
  isAgency: boolean;
  /** Whether the account has the invites license (gates the invite page domain field). */
  hasInvites: boolean;
  /** Redirect domains available for the page-domain selects. */
  redirectDomains: ListResponse<RedirectDomain>;
  /** The redirect domain currently selected for the invite page. */
  refPageRdr?: RedirectDomain;
  /** The redirect domain currently selected for the landing page. */
  mktPageRdr?: RedirectDomain;
  /** Advertiser options for the default advertiser select. */
  advOptions: IAdvertiserOption[];
  /** Action bar setter for right-aligned actions (the save button). */
  setRightActions: ReturnType<typeof useActionBar>['setRightActions'];
  /** Persists dirty changes and toasts the result. */
  handleSave: () => Promise<void>;
  /** Updates the project tags. */
  handleChangeTags: (newValue: string[]) => void;
  /** Updates the invite page redirect domain. */
  handleChangeRefPageRdr: (domain: IRedirectDomain) => void;
  /** Updates the landing page redirect domain. */
  handleChangeMktgPageRdr: (domain: IRedirectDomain) => void;
  /** Updates the default advertiser. */
  handleSelectAdvertiser: (option: IAdvertiserOption) => void;
  /** Captures a manually-typed end date onto the model. */
  handleEndDateInput: (rawEvent: FocusEvent<HTMLInputElement>) => void;
}

/**
 * Parses a manually-typed date (localized or ISO) to an ISO timestamp, or undefined if incomplete.
 * The end date picker routes onChangeRaw here because react-datepicker drops out-of-range typed dates
 * before firing onChange, so they'd otherwise never reach the model.
 */
export function parseTypedDate(rawValue: string | undefined): string | undefined {
  // Day-clicks also fire onChangeRaw but carry no input value; skip them.
  if (!rawValue) {
    return undefined;
  }
  const parsed = moment(rawValue.trim(), [TimeFormat.shortDate, TimeFormat.isoDate], true);
  return parsed.isValid() ? parsed.startOf('day').format(TimeFormat.isoDateTime) : undefined;
}

/**
 * Encapsulates state and business logic for the Project Settings Info page.
 *
 * Handles editing the project's details (name, logo, tags, email label), schedule, page domains,
 * and default advertiser, plus persisting changes via the project model.
 */
export function useEventSettingsInfo(): IUseEventSettingsInfoReturn {
  const account = useAccount();
  const { Events, RedirectDomains } = useStore();
  const { eventId } = useParams<{ eventId: string }>();
  const localUrl = useLocalUrl();
  const { t } = useTranslation();
  const { setRightActions } = useActionBar();
  const hasInvites = useLicense('invites');

  const advOptions: IAdvertiserOption[] = (account.get('ttd')?.subadvertisers || []).map(
    (subadv) => ({
      id: subadv.subadv_id,
      name: subadv.name || '',
    }),
  );

  const event = Events.get(eventId);
  const refPageRdrDomain = event.get('ref_page_rdr_domain');
  const mktgPageRdrDomain = event.get('mktg_page_rdr_domain');

  const redirectDomains = RedirectDomains.list();
  const refPageRdr = redirectDomains.models.find(
    (d) => refPageRdrDomain && d.id === refPageRdrDomain.id,
  );
  const mktPageRdr = redirectDomains.models.find(
    (d) => mktgPageRdrDomain && d.id === mktgPageRdrDomain.id,
  );

  function handleChangeTags(newValue: string[]): void {
    event.set({ tag_ids: newValue });
  }

  function handleChangeRefPageRdr(domain: IRedirectDomain): void {
    event.set({ ref_page_rdr_domain: domain });
  }

  function handleChangeMktgPageRdr(domain: IRedirectDomain): void {
    event.set({ mktg_page_rdr_domain: domain });
  }

  function handleSelectAdvertiser(option: IAdvertiserOption): void {
    event.set({ subadvertiser: option.id });
  }

  function handleEndDateInput(rawEvent: FocusEvent<HTMLInputElement>): void {
    const parsed = parseTypedDate(rawEvent.currentTarget.value);
    if (parsed) {
      event.set({ date_end: parsed });
    }
  }

  async function handleSave(): Promise<void> {
    // submitModelWithErrorHandling treats an isErrored result (or a rejection) as failure: onSuccess
    // fires only on a clean save, and backend errors surface as an error toast.
    await submitModelWithErrorHandling({
      operation: event.patchDirty(),
      onSuccess: (): void => {
        toast(t('Project changes saved.'), { type: 'success' });
      },
      t,
    });
  }

  const errors = event.validate(['name', 'date_start', 'date_end'], false, 'grouped').errors;

  // Date objects for react-datepicker range linking, so neither picker can select past the other.
  const startStr = event.get('date_start');
  const endStr = event.get('date_end');
  const startDate = startStr ? momentToDate(moment.utc(startStr)) : undefined;
  const endDate = endStr ? momentToDate(moment.utc(endStr)) : undefined;

  return {
    event,
    t,
    localUrl,
    errors,
    startDate,
    endDate,
    isDirty: event.isDirty,
    isAgency: !!account.get('is_agency'),
    hasInvites,
    redirectDomains,
    refPageRdr,
    mktPageRdr,
    advOptions,
    setRightActions,
    handleSave,
    handleChangeTags,
    handleChangeRefPageRdr,
    handleChangeMktgPageRdr,
    handleSelectAdvertiser,
    handleEndDateInput,
  };
}
